import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@src/components/ui/button/button';
import toastr from 'toastr';
import { useFormContext } from 'react-hook-form';
import { CancelTaskButton } from '@src/components/common/cancel-task-button/cancel-task-button';
import { cn } from '@src/utils/ui';
import moment from 'moment';
import { LeaveReviewButton } from '@src/components/common/leave-review/leave-review-button';

export const OrderActions = ({
    buyerView,
    orderId,
    fulfillOrder,
    fulfillmentStatus,
    onCancelAndRefund,
    isCreatedInBookNow,
    isEnabled,
    taskDate,
    total,
    buyerHasSubmittedReview,
    ...props
}) => {
    const isPassed = moment().isSameOrAfter(moment.unix(taskDate));
    const showLeaveReview =
        buyerView && !buyerHasSubmittedReview && moment().isBefore(moment.unix(taskDate).add(30, 'days'));
    const [isLoading, setIsLoading] = useState(false);
    const { handleSubmit, getValues, setValue } = useFormContext();

    const handleLeaveReview = () => {
        const { items } = getValues();
        const index = items.findIndex(order => order.id === orderId);
        if (index === -1) handleSubmit();
        else {
            items[index].buyerHasSubmittedReview = true;
            setValue(`items[${index}]`, items[index]);
        }
    };

    if (fulfillmentStatus?.toLowerCase() === 'cancelled') {
        return (
            <>
                {buyerHasSubmittedReview ? (
                    <div className='tw-font-[500] tw-text-[16px] tw-text-[#333333]'>Review Submitted</div>
                ) : null}
                <div className='tw-text-bcaa-red-helpful tw-font-[500] tw-py-[4px] lg:tw-py-0 tw-px-[10px] lg:tw-px-0 tw-grow tw-text-right lg:tw-text-left'>
                    Cancelled
                </div>
            </>
        );
    }

    if (
        fulfillmentStatus?.toLowerCase() === 'completed' ||
        fulfillmentStatus?.toLowerCase() === 'collected' ||
        fulfillmentStatus?.toLowerCase() === 'released'
    ) {
        return (
            <>
                {showLeaveReview ? (
                    <LeaveReviewButton
                        orderId={orderId}
                        {...props}
                        fulfillmentStatus={fulfillmentStatus}
                        onChange={handleLeaveReview}
                    />
                ) : null}
                {buyerHasSubmittedReview ? (
                    <div className='tw-font-[500] tw-text-[16px] tw-text-[#333333]'>Review Submitted</div>
                ) : null}
                <div className='tw-text-[#00B451] tw-font-[500] tw-py-[4px] lg:tw-py-0 tw-px-[10px] lg:tw-px-0 tw-grow tw-text-right lg:tw-text-left'>
                    {fulfillmentStatus}
                </div>
            </>
        );
    }

    if (fulfillmentStatus?.toLowerCase() === 'transferred') {
        return (
            <>
                {showLeaveReview ? (
                    <LeaveReviewButton
                        orderId={orderId}
                        {...props}
                        fulfillmentStatus={fulfillmentStatus}
                        onChange={handleLeaveReview}
                    />
                ) : null}
                {buyerHasSubmittedReview ? (
                    <div className='tw-font-[500] tw-text-[16px] tw-text-[#333333]'>Review Submitted</div>
                ) : null}
                <div className='tw-text-[#FF9500] tw-font-[500] tw-py-[4px] lg:tw-py-0 tw-px-[10px] lg:tw-px-0 tw-grow tw-text-right lg:tw-text-left'>
                    {fulfillmentStatus}
                </div>
            </>
        );
    }

    const handleClick = async e => {
        e.preventDefault();
        e.stopPropagation();
        setIsLoading(true);

        try {
            await fulfillOrder(orderId);
            await handleSubmit();
            toastr.success('Task Completed');
        } catch (err) {
            console.error('Fulfillment error:', err);
            const errorMessage = err?.message || 'Please try again later.';
            toastr.error(errorMessage, 'Oops! Something went wrong.');
        }

        setIsLoading(false);
    };

    const handleCancel = async () => {
        await onCancelAndRefund(orderId);
        await handleSubmit();
    };

    return (
        <>
            <CancelTaskButton
                buyerView={buyerView}
                orderId={orderId}
                taskDate={taskDate}
                total={total}
                onCancelAndRefund={handleCancel}
                className={cn([
                    '[&>div:empty]:tw-d-none tw-flex tw-flex-col tw-items-center tw-gap-[8px]',
                    buyerView ? 'tw-w-full lg:tw-items-end' : 'lg:tw-items-start'
                ])}
                fulfillmentStatus={
                    <div className='tw-text-[#0050C4] tw-font-[500] tw-py-[4px] lg:tw-py-0 tw-px-[10px] lg:tw-px-0 tw-grow tw-text-right lg:tw-text-left'>
                        Upcoming
                    </div>
                }
            />
            {isPassed && !buyerView && !isCreatedInBookNow ? (
                <Button
                    disabled={!isEnabled}
                    loading={isLoading}
                    variant='contained'
                    onClick={handleClick}
                    className='tw-w-auto'
                >
                    MARK AS COMPLETED
                </Button>
            ) : null}
        </>
    );
};

OrderActions.propTypes = {
    buyerView: PropTypes.bool,
    orderId: PropTypes.string.isRequired,
    fulfillOrder: PropTypes.func,
    fulfillmentStatus: PropTypes.string.isRequired,
    onCancelAndRefund: PropTypes.func.isRequired,
    isEnabled: PropTypes.bool.isRequired,
    isCreatedInBookNow: PropTypes.bool.isRequired,
    taskDate: PropTypes.string,
    total: PropTypes.number,
    buyerHasSubmittedReview: PropTypes.bool
};
